import * as React from "react";
import { merge, $, media } from "glamor";
import theme from "styles/theme";
import { formatMessage } from "lib/globalize-util";
import Editor from "draft-js-plugins-editor";
import { defaultSuggestionsFilter, getStyleMergeMixin } from "components/draft-js-mention";
import { Map, Set } from "immutable";
import { Kinds } from "constants/product-collection";
import classNames from "classnames";
import debounce from "lodash/debounce";
import { EditorState, ContentState } from "draft-js";
import { plugins } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-editors/text-editor";
import { KEYBOARD_KEYS } from "constants/keyboard";

const Entry = (props) => {
    const {
        mention,
        theme,
        searchValue, // tslint:disable-line no-unused-vars
        isFocused, // tslint:disable-line no-unused-vars
        // tslint:disable-next-line:trailing-comma
        ...parentProps // prettier-ignore
    } = props;

    return (
        <div {...parentProps}>
            <span className={theme.mentionSuggestionsEntryText}>{mention}</span>
        </div>
    );
};

interface IFieldProps {
    value: string; // Ag-grid rowNode reference to this row
    field: Map<string, any>;
    readOnly: boolean;
    onChange: any;
    bindEditor: any;
    hashtags: Set<string>;
    onKeyDown: (e /*KeyboardEvent<HTMLDivElement>*/) => void;
}

interface IState {
    editorState: any;
    suggestions?: any[];
    focus: boolean;
}

export class TextField extends React.PureComponent<IFieldProps, IState> {
    styles = merge(
        $(" .DraftEditor-root", {
            padding: "10px",
            minWidth: "160px",
            width: "100%",
            zIndex: "100",
        }),
        $(" .DraftEditor-root:hover", {
            background: theme.palette.editing,
        }),
        $(".isFocused .DraftEditor-root", {
            background: theme.palette.editing,
        }),
        $(" .DraftEditor-editorContainer, .public-DraftEditor-content", {
            height: "100%",
            width: "100%",
            overflowY: "auto",
        }),
        $(" .public-DraftEditorPlaceholder-inner", {
            color: theme.palette.placeholderText,
            zIndex: "-1",
        }),
        $(" .editorContainer", {
            position: "relative",
            minWidth: "160px",
            flexGrow: "1",
        }),
        $(" .placeholder", {
            position: "absolute",
            top: "10px",
            left: "10px",
            color: theme.palette.placeholderText,
            fontSize: "14px",
        }),
        media(
            "(max-width: 480px)",
            $(" .placeholder", {
                fontSize: "12px",
            })
        ),
        ...getStyleMergeMixin()
    );

    private editor;

    private mentionPlugin;
    private plugins;
    private isMobileiOS;

    constructor(props: IFieldProps) {
        super(props);
        this.state = {
            editorState: EditorState.createWithContent(ContentState.createFromText(props.value || "")),
            suggestions: props.hashtags.toJS(),
            focus: false,
        };

        this.mentionPlugin = plugins[0];
        this.plugins = plugins;
        this.isMobileiOS = /iPad|iPhone/.test(window.navigator.userAgent);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        // Do not update if typing
        if (!this.state.focus) {
            this.setState({
                editorState: EditorState.createWithContent(ContentState.createFromText(nextProps.value || "")),
            });
        }
    }

    handleFocus = () => {
        this.setState({ focus: true, suggestions: this.props.hashtags.toJS() });
    };

    handleBlur = () => {
        this.setState({ focus: false });
        this.handleSaveChanges.cancel();
        const { field, onChange, value = "" } = this.props;
        const { editorState } = this.state;
        const displayedValue = editorState.getCurrentContent().getPlainText();
        if (value !== displayedValue) {
            onChange({ value: displayedValue, field });
        }
    };

    handleClick = () => {
        process.nextTick(() => {
            if (this.editor && !this.state.focus) {
                this.editor.focus();
            }
        });
    };

    getEditorValue() {
        return this.state.editorState.getCurrentContent().getPlainText().trim();
    }

    render() {
        const { field, readOnly } = this.props;
        const { editorState, focus, suggestions } = this.state;
        const { MentionSuggestions } = this.mentionPlugin;
        const hasValue = editorState.getCurrentContent().getPlainText().trim() || focus;
        return (
            <div
                {...this.styles}
                className={classNames("fieldContainer", { isFocused: focus, editable: !readOnly })}
                onKeyDown={this.handleKeyDown}
                onClick={this.handleClick}
                data-field={field.get("field_name")}
            >
                <span className="fieldLabel">{field.get("display_name")}</span>
                <div className="editorContainer">
                    <Editor
                        placeholder={readOnly || this.isMobileiOS ? null : formatMessage("Type to set a value")}
                        readOnly={readOnly}
                        editorState={editorState}
                        onChange={this.handleChange}
                        plugins={this.plugins}
                        ref={this.bindEditor}
                        onFocus={this.handleFocus}
                        onBlur={this.handleBlur}
                        handleReturn={this.handleReturn}
                        webDriverTestID={field.get("field_name")}
                    />
                    {!hasValue && this.isMobileiOS && !readOnly && (
                        <span className="placeholder">{formatMessage("Type to set a value")}</span>
                    )}
                </div>
                {focus && (
                    <MentionSuggestions
                        onSearchChange={this.handleSearchChange}
                        suggestions={suggestions}
                        entryComponent={Entry}
                        focus={focus}
                    />
                )}
            </div>
        );
    }

    private bindEditor = (ref) => {
        const { field, bindEditor } = this.props;
        bindEditor(ref, field.get("field_name"));
        return (this.editor = ref);
    };

    private handleReturn = () => {
        const kind = this.props.field.get("kind");
        if (kind === Kinds.MULTILINE_TEXT) {
            return "not-handled";
        }
        return "handled";
    };

    private handleSearchChange = ({ value }) => {
        const suggestions = defaultSuggestionsFilter(value, this.props.hashtags ? this.props.hashtags.toJS() : []);
        this.setState({
            suggestions,
        });
        return suggestions;
    };

    private handleChange = (editorState) => {
        const previousValue = this.state.editorState.getCurrentContent().getPlainText();
        this.setState({
            editorState,
        });

        const value = editorState.getCurrentContent().getPlainText();
        if (previousValue !== value) {
            const { field } = this.props;
            this.handleSaveChanges({ value, field });
        }
    };

    // tslint:disable-next-line: member-ordering
    private handleSaveChanges = debounce(({ value, field }) => this.props.onChange({ value, field }), 600);

    private handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        switch (e.key) {
            case KEYBOARD_KEYS.TAB:
                // Suggestions will cause defaultPrevented to be true if using key events
                if (!e.defaultPrevented) {
                    this.props.onKeyDown(e);
                }
                break;
            case KEYBOARD_KEYS.ENTER:
                // Press enter for multiline field should be reserved
                if (this.props.field.get("kind") !== Kinds.MULTILINE_TEXT) {
                    this.props.onKeyDown(e);
                }
                break;
            default:
        }
    };
}
